'use client';

import { useEffect, useRef } from 'react';
import styles from './StackShowcase.module.scss';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export interface StackStep {
  num: string;
  title: string;
  description: string;
  color: string;
  colorSoft: string;
  image?: string;
}

export interface StackShowcaseProps {
  steps?: StackStep[];
  footerEnd?: string;
}

const DEFAULT_STEPS: StackStep[] = [
  {
    num: '01',
    title: 'Discovery & Data',
    description:
      "Nous commençons par un audit rapide et une compréhension fine de votre métier. Vous n'avez pas à rédiger de cahier des charges exhaustif — nous venons chercher les informations qui comptent et posons les fondations solides.",
    color: '#b98745',
    colorSoft: 'rgba(185, 135, 69, 0.12)',
  },
  {
    num: '02',
    title: 'Product Design',
    description:
      "Wireframes, parcours et maquettes haute-fidélité. Chaque décision est validée avec vous, mais nous ne bloquons pas l'avancement sur des allers-retours infinis. L'objectif : avancer vite sans sacrifier la qualité.",
    color: '#1d6fb8',
    colorSoft: 'rgba(29, 111, 184, 0.12)',
  },
  {
    num: '03',
    title: 'Software Engineering',
    description:
      'Architecture robuste, code propre et stack moderne. Nous construisons en intégration continue pour que vous puissiez voir évoluer votre produit en temps réel, sans attendre le dernier jour.',
    color: '#071f35',
    colorSoft: 'rgba(7, 31, 53, 0.08)',
  },
  {
    num: '04',
    title: 'Agile Delivery',
    description:
      "Sprints courts, démos régulières et visibilité totale. Nous gérons l'orchestration des tâches et des ressources — vous ne vous préoccupez que des validations stratégiques et des métriques de valeur business.",
    color: '#b98745',
    colorSoft: 'rgba(185, 135, 69, 0.12)',
  },
  {
    num: '05',
    title: 'Testing & QA',
    description:
      'Tests automatisés, revues de code et assurance qualité systématique. Chaque livrable est validé avant mise en production pour garantir fiabilité, performance et expérience utilisateur irréprochable.',
    color: '#1d6fb8',
    colorSoft: 'rgba(29, 111, 184, 0.12)',
  },
  {
    num: '06',
    title: 'Itération & Métriques',
    description:
      "Métriques définies ensemble, itérations data-driven et optimisation continue. Votre produit s'améliore après le lancement, piloté par des indicateurs concrets que nous partageons et analysons régulièrement.",
    color: '#071f35',
    colorSoft: 'rgba(7, 31, 53, 0.08)',
  },
];

const DEFAULT_FOOTER_END = 'de conception à livraison';

export const StackShowcase = ({
  steps = DEFAULT_STEPS,
  footerEnd = DEFAULT_FOOTER_END,
}: StackShowcaseProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const cardCount = steps.length;

  useEffect(() => {
    if (typeof window === 'undefined' || !wrapperRef.current) {
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || cardCount <= 1) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const wrapper = wrapperRef.current;
    const ctx = gsap.context(() => {
      const stackPin = wrapper.querySelector('[data-stack-pin]');
      if (!stackPin) {
        return;
      }

      /* All cards exactly superposed */
      gsap.set('[data-stack-card]', {
        opacity: (i: number) => (i === 0 ? 1 : 0),
        scale: 1,
        x: 0,
        y: 0,
        rotate: 0,
        zIndex: (i: number) => i,
      });

      gsap.set('[data-stack-text]', { opacity: 0, y: 0 });
      gsap.set('[data-stack-text="0"]', { opacity: 1, y: 0 });
      gsap.set('[data-stack-tick]', { scaleX: 0, transformOrigin: 'left center' });
      gsap.set('[data-stack-tick="0"]', { scaleX: 1 });

      /* CSS sticky handles the "pinning" natively — GSAP only scrubs the fades */
      const stackTl = gsap.timeline({
        scrollTrigger: {
          trigger: stackPin,
          start: 'top top',
          end: 'bottom bottom',
          scrub: true,
        },
      });

      /* Animate through each step — perfectly linear distribution */
      for (let step = 1; step < cardCount; step++) {
        const t = step / cardCount; // 0.2, 0.4, 0.6, 0.8
        const duration = 0.12;

        /* Cards — pure opacity fade, no translate */
        for (let i = 0; i < cardCount; i++) {
          const isCurrent = i === step;
          const isPast = i < step;

          if (isCurrent) {
            /* New card fades in on top */
            stackTl.fromTo(
              `[data-stack-card="${i}"]`,
              { opacity: 0, scale: 0.98 },
              { opacity: 1, scale: 1, zIndex: 20 + i, duration, ease: 'none' },
              t
            );
          } else if (isPast) {
            /* Past cards fade back but stay perfectly in place */
            const depth = step - i;
            stackTl.to(
              `[data-stack-card="${i}"]`,
              {
                opacity: Math.max(0.12, 0.55 - depth * 0.1),
                scale: 1,
                x: 0,
                y: 0,
                rotate: 0,
                zIndex: 10 - depth,
                duration,
                ease: 'none',
              },
              t
            );
          }
          /* Future cards stay hidden */
        }

        /* Text — pure opacity cross-fade, zero movement */
        for (let i = 0; i < cardCount; i++) {
          stackTl.to(
            `[data-stack-text="${i}"]`,
            {
              opacity: i === step ? 1 : 0,
              y: 0,
              duration: duration * 0.6,
              ease: 'none',
            },
            t
          );
        }

        /* Progress ticks — snappy but linear */
        for (let i = 0; i < cardCount; i++) {
          stackTl.to(
            `[data-stack-tick="${i}"]`,
            { scaleX: i <= step ? 1 : 0, duration: duration * 0.4, ease: 'none' },
            t
          );
        }
      }
    }, wrapper);

    return () => ctx.revert();
  }, [cardCount]);

  return (
    <div ref={wrapperRef}>
      <div className={styles.stackPinWrapper} data-stack-pin>
        <div className={styles.stackStage} data-stack-stage>
          <div className={styles.stackContent}>
            {/* ── Left: Image Stack ── */}
            <div className={styles.stackColumn}>
              <div className={styles.imageStack}>
                {steps.map((step, i) => (
                  <div
                    key={i}
                    className={styles.stackCard}
                    data-stack-card={i}
                    style={{
                      boxShadow: `0 20px 60px ${step.colorSoft}, 0 4px 12px rgba(7,31,53,0.08)`,
                    }}
                  >
                    {step.image ? (
                      <img src={step.image} alt={step.title} />
                    ) : (
                      <div
                        className={styles.coloredSquare}
                        style={{
                          background: `linear-gradient(135deg, ${step.colorSoft}, ${step.color}22)`,
                          borderColor: `${step.color}40`,
                        }}
                      >
                        <span className={styles.squareNum}>{step.num}</span>
                        <span className={styles.squareLabel} style={{ color: step.color }}>
                          {step.title}
                        </span>
                        <small className={styles.squareHint}>Image à venir</small>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Decorative blueprint lines behind stack */}
              <svg className={styles.stackDecorSvg} viewBox="0 0 400 400" aria-hidden="true">
                <circle
                  cx="200"
                  cy="200"
                  r="160"
                  fill="none"
                  stroke="rgba(185,135,69,0.08)"
                  strokeWidth="1"
                  strokeDasharray="8 6"
                />
                <circle
                  cx="200"
                  cy="200"
                  r="120"
                  fill="none"
                  stroke="rgba(29,111,184,0.06)"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                <line
                  x1="40"
                  y1="200"
                  x2="360"
                  y2="200"
                  stroke="rgba(7,31,53,0.05)"
                  strokeWidth="1"
                />
                <line
                  x1="200"
                  y1="40"
                  x2="200"
                  y2="360"
                  stroke="rgba(7,31,53,0.05)"
                  strokeWidth="1"
                />
              </svg>
            </div>

            {/* ── Right: Text Content ── */}
            <div className={styles.textColumn}>
              <div className={styles.textWrapper}>
                {steps.map((step, i) => (
                  <div key={i} className={styles.textStep} data-stack-text={i}>
                    <span className={styles.stepNum} style={{ color: step.color }}>
                      {step.num}
                    </span>
                    <h3 className={styles.stepTitle}>{step.title}</h3>
                    <p className={styles.stepDesc}>{step.description}</p>
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div className={styles.progressBar}>
                {steps.map((step, i) => (
                  <div key={i} className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      data-stack-tick={i}
                      style={{ background: step.color }}
                    />
                  </div>
                ))}
              </div>

              {/* Total steps indicator */}
              <div className={styles.stepsIndicator}>
                <span>{steps.length} phases</span>
                <span className={styles.indicatorLine} />
                <span>{footerEnd}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
